import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiMenu, FiX } from 'react-icons/fi';
import Sidebar from './Sidebar';
import NotificationBell from './NotificationBell';
import ChangePasswordModal from './ChangePasswordModal';
import './Layout.css';

const PAGE_META = {
  '/dashboard':       { title: 'Dashboard',                  subtitle: "Welcome back! Here's what's happening." },
  '/projects':        { title: 'Project Management',         subtitle: 'Track and manage the complete project lifecycle.' },
  '/clients':         { title: 'Customer Management',        subtitle: 'Maintain all customer information in a centralized location.' },
  '/quotations':      { title: 'Quotation Management',       subtitle: 'Create, manage and track customer quotations.' },
  '/material-planning':{ title: 'Material Planning',         subtitle: 'Identify all materials required before project execution.' },
  '/purchase':        { title: 'Vendor-Wise Material Receipt Tracking', subtitle: 'Real-time visibility of material receipt status for better planning and zero shortages.' },
  '/site-execution':  { title: 'Site Execution Management',  subtitle: 'Monitor daily project execution and site activities.' },
  '/quality-check':   { title: 'Quality Check Management',   subtitle: 'Ensure quality standards and track approvals across all project activities.' },
  
  '/task-planner':    { title: 'Task Planner',               subtitle: 'Manage daily activities and plan tasks for better execution.' },
  '/attendance':      { title: 'Attendance',                 subtitle: 'Track employee attendance and work hours.' },
  '/holidays':        { title: 'Holidays',                   subtitle: 'Manage public and company holidays.' },
  '/users':           { title: 'User Management',            subtitle: 'Manage system users and their access.' },
  '/roles':           { title: 'Roles & Permissions',        subtitle: 'Configure roles and module access.' },
  '/modules':         { title: 'Modules',                    subtitle: 'Manage system modules.' },
  '/activity-logs':   { title: 'Activity Logs',              subtitle: 'Monitor all system activity.' },
  '/finance':         { title: 'Finance & Payment Tracking', subtitle: 'Track project payments and monitor collection status.' },
  '/mail-logs':          { title: 'Mail Logs',        subtitle: 'View all outgoing emails sent by the system.' },
  '/reports':            { title: 'Reports',          subtitle: 'View and export business reports and analytics.' },
  '/masters/stock':      { title: 'Stock Master',     subtitle: 'Manage in-house stock items available for sale to customers.' },
  '/customer-orders':    { title: 'Customer Orders',  subtitle: 'Track customer orders fulfilled from stock or purchased from vendors.' },
  '/workshop':           { title: 'Workshop',         subtitle: 'Send item dimensions to the workshop team and track fabrication status.' },
};

const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444'];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const location = useLocation();
  const meta = PAGE_META[location.pathname] || { title: 'Creative Glass Tec', subtitle: '' };

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  function handleToggle() {
    if (isMobile) setMobileOpen(v => !v);
    else setCollapsed(v => !v);
  }

  return (
    <div className="layout">
      {/* Mobile overlay */}
      {isMobile && (
        <div className={`sidebar-overlay ${mobileOpen ? 'active' : ''}`} onClick={() => setMobileOpen(false)} />
      )}

      <Sidebar
        collapsed={isMobile ? false : collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        isMobile={isMobile}
      />

      <main className={`main-content ${!isMobile && collapsed ? 'sidebar-collapsed' : ''}`}>
        {/* ── Topbar ── */}
        <header className="topbar">
          <div className="topbar-left">
            <button className="topbar-toggle" onClick={handleToggle} title="Toggle menu">
              {(isMobile ? mobileOpen : !collapsed) ? <FiX size={18} /> : <FiMenu size={18} />}
            </button>
            <div className="topbar-titles">
              <h2 className="topbar-title">{meta.title}</h2>
              {meta.subtitle && <p className="topbar-subtitle">{meta.subtitle}</p>}
            </div>
          </div>
          <div className="topbar-right">
            <NotificationBell />
            <div className="topbar-user">
              <div className="topbar-avatar" style={{ background: avatarColor(user?.name) }}>
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div className="topbar-user-info">
                <p className="topbar-user-name">{user?.name}</p>
                <p className="topbar-user-role">{user?.role?.name || user?.role || 'User'}</p>
              </div>
            </div>
          </div>
        </header>

        {/* ── Page Content ── */}
        <div className="page-body">
          {children}
        </div>
      </main>

      {/* Force password change on first login */}
      {user?.mustChangePassword && <ChangePasswordModal forced={true} />}
    </div>
  );
}
